// Pure decision helper for the chat transcription error surface (LAN-380).
//
// It has no imports and touches no DOM or network, so audio_recording_service.js
// can ask it two questions that are cheap to test on their own:
//
//   * planOutcome  - did this transcription attempt fail, what do we tell the
//                    user, and do the recording controls go back on?
//   * buildReport  - the non-identifying facts about a failure. It NEVER carries
//                    transcript text, audio, or message content: it copies three
//                    fields by name and ignores everything else it is handed.
//
// Messages are plain English on purpose. javascript.chat.* keys fall back
// silently when a locale is missing, which is exactly the silence LAN-380 is
// about, so the strings live here where they cannot go missing.

const REJECTED = 'rejected';
const FAILED = 'failed';
const MALFORMED = 'malformed';
const OK = 'ok';

const MESSAGES = {
  [REJECTED]: "We couldn't transcribe that recording - the speech service turned it down. Please record again, or type your reply.",
  [FAILED]: "We couldn't reach the speech service. Check your connection, then record again or type your reply.",
  [MALFORMED]: "The speech service answered, but sent no text back. Please record again, or type your reply."
};

function toStatus(value) {
  const status = Number(value);
  return Number.isFinite(status) ? status : 0;
}

function toText(value) {
  return (value === undefined || value === null) ? null : String(value);
}

function classify(status, hasText) {
  if (status >= 200 && status < 300) {
    return hasText ? OK : MALFORMED;
  }
  if (status >= 400 && status < 500) {
    return REJECTED;
  }
  return FAILED;
}

const TranscriptionErrorReporter = {
  REJECTED: REJECTED,
  FAILED: FAILED,
  MALFORMED: MALFORMED,
  OK: OK,

  // Decide what happens after a transcription attempt.
  // Returns { failed, userMessage, restoreControls, kind }.
  // restoreControls is true on every path: the record button must never be
  // left disabled, which is the permanently dead microphone in LAN-380.
  planOutcome(result) {
    const input = result || {};
    const status = toStatus(input.status);
    const kind = classify(status, Boolean(input.hasText) && input.code !== 'no_speech');

    return {
      failed: kind !== OK,
      userMessage: kind === OK ? '' : MESSAGES[kind],
      restoreControls: true,
      kind: kind
    };
  },

  // Non-identifying description of a failure. Only these three fields survive.
  buildReport(details) {
    const input = details || {};

    return {
      status: toStatus(input.status),
      transcription_mode: toText(input.transcriptionMode),
      language: toText(input.language)
    };
  }
};

export default TranscriptionErrorReporter;
